import { useEffect, useMemo, useState } from 'react';

import { DEFAULT_PAGE_SIZE } from '@/core/constants/app';
import { Dcr } from '@/core/graphql/generated/types';
import { useDebouncedValue } from '@/utils/useDebouncedValue';
import { useIdle } from '@/utils/useIdle';
import { useVisible } from '@/utils/useVisible';

import { useGetDcrListQuery } from '../_graphql/query/GetDCRList.hook';
import { computeDcrStatus } from '../_utils/computeDcrStatus';
import { type StatusFilterValue } from '../StatusFilter/StatusFilter';

const POLL_INTERVAL = 30000;
const FAST_POLL_INTERVAL = 2500;
const FRESH_PROCESSING_WINDOW_MS = 60000;
const SEARCH_DEBOUNCE_MS = 300;

const hasFreshProcessingDcr = (
	dcrs: Pick<Dcr, 'createdAt' | 'displayStatus'>[]
) =>
	dcrs.some(d => {
		if (!d.createdAt) return false;
		const ageMs = Date.now() - new Date(d.createdAt).getTime();
		return (
			ageMs < FRESH_PROCESSING_WINDOW_MS && computeDcrStatus(d) === 'processing'
		);
	});

export const useMyDcrs = (
	searchTerm: string,
	selectedStatuses: StatusFilterValue[],
	page: number
) => {
	const visible = useVisible();
	const idle = useIdle(300000); // Note (Ben): Inactive after 5 minutes

	const [activeDcrId, setActiveDcrId] = useState<Dcr['id'] | null>(null);
	const paginationModel = useMemo(
		() => ({ page: page - 1, pageSize: DEFAULT_PAGE_SIZE }),
		[page]
	);

	// Used to conditionally render skeleton for status filter
	const [statusFilterLoading, setStatusFilterLoading] = useState(true);

	const debouncedSearchTerm = useDebouncedValue(
		searchTerm.trim(),
		SEARCH_DEBOUNCE_MS
	);

	const statuses = selectedStatuses.length > 0 ? selectedStatuses : undefined;

	const [prevSettledResults, setPrevSettledResults] = useState<
		typeof results | undefined
	>(undefined);

	// Note (Ben): Fresh (<1min old) DCRs still in Processing get fast-polled so the
	// table flips to its real status quickly. Older Processing DCRs may be stuck,
	// so we don't burn requests on them.
	const hasFreshProcessing = hasFreshProcessingDcr(
		prevSettledResults?.results ?? []
	);

	const [, forceFreshnessRecheck] = useState(0);
	useEffect(() => {
		if (!hasFreshProcessing) return;
		const id = window.setInterval(
			() => forceFreshnessRecheck(t => t + 1),
			FAST_POLL_INTERVAL
		);
		return () => window.clearInterval(id);
	}, [hasFreshProcessing]);

	const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

	const shouldPoll = visible && !idle;
	const { data, previousData, loading } = useGetDcrListQuery({
		pollInterval: shouldPoll
			? hasFreshProcessing
				? FAST_POLL_INTERVAL
				: POLL_INTERVAL
			: 0,
		variables: {
			// The backend rejects explicit `null` on optional input fields (only
			// omission is allowed), so absent search/status filters must be left
			// out of the object entirely rather than set to null.
			searchInput: {
				searchString: debouncedSearchTerm || undefined,
				statuses
			},
			paginationInput: {
				page: paginationModel.page + 1,
				pageSize: paginationModel.pageSize
			}
		}
	});

	const results =
		data?.getDcrs.__typename === 'DCRResults' ? data.getDcrs : undefined;
	const previousResults =
		previousData?.getDcrs.__typename === 'DCRResults'
			? previousData.getDcrs
			: undefined;

	const displayResults = results ?? previousResults;
	const dcrs = useMemo(() => displayResults?.results ?? [], [displayResults]);
	const rowCount = displayResults?.total ?? 0;
	const hasNoDcrs = rowCount === 0;
	const facetCounts = displayResults?.statusFacets ?? [];
	const hasSettledActiveFilters =
		debouncedSearchTerm !== '' || selectedStatuses.length > 0;

	// --- Status filter skeleton turns on when the search changes (not status selection)
	const [
		prevDebouncedSearchTermForLoading,
		setPrevDebouncedSearchTermForLoading
	] = useState(debouncedSearchTerm);
	if (prevDebouncedSearchTermForLoading !== debouncedSearchTerm) {
		setPrevDebouncedSearchTermForLoading(debouncedSearchTerm);
		setStatusFilterLoading(true);
	}

	// On every settled fetch: refresh freshness tracking (by keeping
	// `prevSettledResults` current) and turn off the status filter skeleton
	if (results && results !== prevSettledResults) {
		setPrevSettledResults(results);
		setStatusFilterLoading(false);
	}

	if (results && !hasLoadedOnce) {
		setHasLoadedOnce(true);
	}
	const isInitialLoading = loading && !hasLoadedOnce;

	const activeDcr = useMemo(
		() => (dcrs.find(d => d.id === activeDcrId) ?? null) as Dcr | null,
		[dcrs, activeDcrId]
	);

	return {
		setActiveDcrId,
		paginationModel,
		dcrs,
		loading,
		isInitialLoading,
		hasNoDcrs,
		hasSettledActiveFilters,
		debouncedSearchTerm,
		statusFilterLoading,
		activeDcr,
		rowCount,
		facetCounts
	};
};
